import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Printer } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { GARDIENS, COLLECTIONS } from "@/data/gardiens";
import { PILL_BTN } from "@/lib/pill";
import { downloadGardienCard, downloadFamilierCard } from "@/lib/gardienCard";

// Petit bouton d'impression flottant sur une carte (sans déclencher le lien)
const PrintBtn = ({ g, familier = false }) => {
  const [busy, setBusy] = useState(false);
  const print = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      await (familier ? downloadFamilierCard(g) : downloadGardienCard(g));
    } catch {
      toast.error("La carte n'a pas pu être créée.");
    }
    setBusy(false);
  };
  return (
    <button
      onClick={print}
      disabled={busy}
      data-testid={familier ? `familier-card-print-${g.id}` : `gardien-card-print-${g.id}`}
      title={familier ? `Imprimer la carte de ${g.familiar.name}` : `Imprimer la carte de ${g.name}`}
      className="absolute top-4 right-4 z-10 p-2.5 rounded-full bg-background/90 border border-warm/40 text-warm hover:bg-warm hover:text-warm-foreground hover:scale-110 transition-all duration-300 disabled:opacity-60 shadow-sm"
    >
      <Printer className="w-4 h-4" />
    </button>
  );
};

const COLLECTION_AFFICHES = {
  souvenirs: { src: "/gardiens/affiche-souvenirs.jpg?v=4", alt: "Affiche originale — Les Gardiens des Souvenirs" },
  forces: { src: "/gardiens/affiche-forces.jpg?v=4", alt: "Affiche originale — Les Gardiens des Forces Intérieures" },
};

const FamilierCard = ({ g, i, cols }) => (
  <Reveal delay={(i % cols) * 0.08}>
    <Link to={`/gardiens/${g.id}`} data-testid={`familier-card-${g.id}`}
      className="group relative block bg-card rounded-[1.75rem] border border-warm/30 p-7 text-center h-full hover:-translate-y-1.5 hover:shadow-[0_20px_45px_rgb(0,0,0,0.10)] transition-all duration-500">
      <PrintBtn g={g} familier />
      <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{g.name} — {g.title}</p>
      <div className="relative mx-auto w-36 h-36 mt-4">
        <div className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 3px ${g.color}44, 0 0 0 7px ${g.color}1A` }} />
        <img src={g.familiar.image} alt={`${g.familiar.name} — ${g.familiar.title}`} className="w-36 h-36 rounded-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <p className="mt-5 inline-block rounded-full bg-warm text-warm-foreground text-lg font-serif-display px-6 py-1.5">{g.familiar.name}</p>
      <p className="mt-2 font-serif-display italic text-foreground/75">{g.familiar.title}</p>
      <img src="/logo-pont.png" alt="Passerelle" className="mx-auto mt-5 w-14 opacity-70" data-testid={`familier-card-logo-${g.id}`} />
    </Link>
  </Reveal>
);

// Cartes individuelles des fidèles compagnons d'une collection
const FamiliersGrid = ({ col }) => {
  const cols = col.id === "forces" ? 4 : 3;
  return (
    <div className="mt-14" data-testid={`${col.id}-familiers`}>
      <Reveal>
        <p className="text-xs uppercase tracking-[0.25em] text-warm mb-2 text-center">Les Fidèles Compagnons</p>
        <p className="font-serif-display italic text-2xl text-center text-foreground/80 mb-8">des Gardiens {col.id === "forces" ? "des Forces Intérieures" : "des Souvenirs"}</p>
      </Reveal>
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${cols === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-8`}>
        {GARDIENS.filter((g) => g.collection === col.id && g.familiar).map((g, i) => (
          <FamilierCard key={g.id} g={g} i={i} cols={cols} />
        ))}
      </div>
    </div>
  );
};

const GardienCard = ({ g, i }) => (
  <Reveal delay={(i % 3) * 0.1}>
    <Link to={`/gardiens/${g.id}`} data-testid={`gardien-card-${g.id}`} className="group relative block bg-card rounded-[2rem] border border-warm/25 p-8 text-center h-full hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgb(0,0,0,0.10)] transition-all duration-500">
      <PrintBtn g={g} />
      <div className="relative mx-auto w-40 h-40">
        <div className="absolute inset-0 rounded-full" style={{ boxShadow: `0 0 0 4px ${g.color}33, 0 0 0 8px ${g.color}1A` }} />
        <img src={g.image} alt={`${g.name} — ${g.title}`} className="w-40 h-40 rounded-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
      </div>
      <p className="mt-6 text-xs uppercase tracking-[0.25em]" style={{ color: g.color }}>{g.value}</p>
      <h3 className="font-serif-display text-3xl mt-2">{g.name}</h3>
      <p className="text-sm text-muted-foreground mt-1">{g.title}</p>
      <p className="font-serif-display italic text-lg text-foreground/75 leading-relaxed mt-5 flex-1">« {g.quote} »</p>
    </Link>
  </Reveal>
);

const Gardiens = () => (
  <div className="pt-32">
    {/* HERO */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.3em] text-warm mb-5">Les Gardiens des Souvenirs et des forces intérieures</p>
        <h1 className="font-serif-display italic text-5xl sm:text-6xl font-light max-w-3xl leading-tight">
          Dix-sept veilleurs de belles émotions
        </h1>
        <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
          Chaque Gardien veille sur une émotion précieuse ou une force intérieure — la mémoire, la joie,
          l'amour, la sérénité, la découverte… Découvrez celui qui accompagnera le mieux votre proche,
          grâce à notre petit quiz tout en douceur.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/quiz-gardien" data-testid="gardiens-quiz-btn" className={PILL_BTN}>
            <Sparkles className="w-4 h-4" /> Trouver son Gardien <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="mt-12 rounded-[2rem] overflow-hidden border border-warm/30 shadow-[0_30px_80px_rgb(0,0,0,0.12)]">
          <img src="/gardiens/affiche.jpg?v=4" alt="Les Gardiens des Souvenirs et des Forces Intérieures — les dix-sept personnages" className="w-full h-auto block" data-testid="gardiens-affiche" />
        </div>
      </Reveal>
    </section>

    {/* GALERIE — 2 collections */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 space-y-20">
      {COLLECTIONS.map((col) => (
        <div key={col.id} data-testid={`collection-${col.id}`}>
          <Reveal>
            <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light">{col.label}</h2>
            <p className="mt-4 text-foreground/70 max-w-2xl leading-relaxed">{col.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-8 rounded-[2rem] overflow-hidden border border-warm/30 shadow-[0_20px_60px_rgb(0,0,0,0.10)]">
              <img src={COLLECTION_AFFICHES[col.id].src} alt={COLLECTION_AFFICHES[col.id].alt} className="w-full h-auto block" data-testid={`collection-affiche-${col.id}`} />
            </div>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
            {GARDIENS.filter((g) => g.collection === col.id).map((g, i) => <GardienCard key={g.id} g={g} i={i} />)}
          </div>
          {col.id === "souvenirs" && (
            <Reveal delay={0.1}>
              <figure className="mt-12 max-w-sm mx-auto" data-testid="passerelle-card">
                <div className="rounded-[2rem] overflow-hidden border border-warm/40 shadow-[0_24px_60px_rgb(0,0,0,0.14)] hover:-translate-y-1.5 transition-transform duration-500">
                  <img src="/gardiens/carte-passerelle.jpg?v=4" alt="La carte Passerelle — Aidons-les à sortir du brouillard en traversant la Passerelle" className="w-full h-auto block" />
                </div>
                <figcaption className="mt-5 text-center font-serif-display italic text-xl text-foreground/75">« Aidons-les à sortir du brouillard en traversant la Passerelle »</figcaption>
              </figure>
            </Reveal>
          )}
          {/* Cartes individuelles des fidèles compagnons */}
          <FamiliersGrid col={col} />
        </div>
      ))}
    </section>

    {/* CTA QUIZ */}
    <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-28">
      <Reveal>
        <div className="rounded-[2.5rem] bg-warm text-warm-foreground px-8 sm:px-14 py-16 grain text-center">
          <Sparkles className="w-10 h-10 mx-auto" strokeWidth={1.4} />
          <h2 className="font-serif-display italic text-4xl sm:text-5xl font-light mt-6">Quel Gardien veillera sur votre proche ?</h2>
          <p className="mt-5 max-w-xl mx-auto text-warm-foreground/90 leading-relaxed">
            Cinq questions sur ses goûts et sa personnalité — et son Gardien se révèle,
            avec des activités et des objets choisis rien que pour lui ou elle.
          </p>
          <Link to="/quiz-gardien" data-testid="gardiens-quiz-cta" className="inline-block mt-9 rounded-full bg-background text-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">
            Faire le quiz (2 minutes)
          </Link>
        </div>
      </Reveal>
    </section>
  </div>
);

export default Gardiens;
